import { useParams, Link } from 'react-router-dom'
import { useSport } from '@/hooks/useSport'
import { useApi } from '@/hooks/useApi'
import { fetchNewsArticle } from '@/lib/api'
import { Skeleton } from '@/components/ui/Skeleton'

export default function NewsArticleShow() {
  const { slug } = useParams<{ slug: string }>()
  const { sport } = useSport()

  const article = useApi(
    () => fetchNewsArticle(sport, slug!).then(d => d.news_article),
    [sport, slug],
  )

  if (article.loading) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Skeleton variant="text" lines={1} className="h-8 w-2/3" />
        <Skeleton variant="card" className="h-64" />
        <Skeleton variant="text" lines={6} />
      </div>
    )
  }

  if (!article.data) {
    return (
      <div className="card p-12 text-center">
        <p className="text-slate-400">Article not found.</p>
        <Link to={`/${sport}/news`} className="text-accent text-sm mt-2 inline-block">
          Back to news
        </Link>
      </div>
    )
  }

  const a = article.data

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link
        to={`/${sport}/news`}
        className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-accent transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to news
      </Link>

      <article>
        {a.image_url && (
          <div className="rounded-xl overflow-hidden mb-6">
            <img src={a.image_url} alt={a.title} className="w-full h-auto" />
          </div>
        )}

        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-bold uppercase tracking-wider text-accent bg-accent/10 px-2 py-1 rounded">
            {a.source}
          </span>
          {a.published_at && (
            <time className="text-sm text-slate-500">
              {new Date(a.published_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
          )}
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3">{a.headline || a.title}</h1>

        {a.description && (
          <p className="text-lg text-slate-300 leading-relaxed mb-6">{a.description}</p>
        )}

        {a.source_url && (
          <a
            href={a.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
          >
            Read full article on {a.source}
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        )}
      </article>
    </div>
  )
}
